import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { RadioGroup } from '@/components/ui/radio-group';
import {
  type ExtraGuestParams,
  type ExtraGuestBasis,
  type RuleFormProps,
  majorStrToMinorOpt,
  minorToMajorStr,
} from '@/lib/pricingRules';

// extra_guest: a single amount plus what it is charged per. Only ADULTS beyond
// the apartment's base occupancy are charged — children and infants never are.
// Direct bookings only: OTA channels charge their own extra-guest fees, set on
// the channel side.

const BASIS_OPTIONS: { value: ExtraGuestBasis; label: string }[] = [
  { value: 'per_night', label: 'Per night' },
  { value: 'per_stay', label: 'Once per stay' },
];

interface Draft {
  amount: string;
  basis: ExtraGuestBasis;
}

function paramsToDraft(p: ExtraGuestParams): Draft {
  return { amount: minorToMajorStr(p.amount_minor), basis: p.basis ?? 'per_night' };
}

function draftToParams(d: Draft): ExtraGuestParams {
  const amount = majorStrToMinorOpt(d.amount);
  return { amount_minor: typeof amount === 'number' ? amount : 0, basis: d.basis };
}

export function ExtraGuestRuleForm({ params, onChange }: RuleFormProps<ExtraGuestParams>) {
  const [draft, setDraft] = useState<Draft>(() => paramsToDraft(params));

  function patch(p: Partial<Draft>) {
    const next = { ...draft, ...p };
    setDraft(next);
    onChange(draftToParams(next));
  }

  return (
    <div className="flex flex-col gap-3">
      <Input
        label="Per adult over base (€)"
        type="number"
        min={0}
        step="0.01"
        value={draft.amount}
        onChange={(e) => patch({ amount: e.target.value })}
      />

      <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
        <legend className="text-sm text-muted">Charged</legend>
        {/* No explicit `name`: RadioGroup defaults to a per-instance useId(). Two
            extra_guest rules can never coexist on one strategy (migration 0039's
            pricing_rules_one_per_type UNIQUE (strategy_id, type)), so this isn't
            guarding against instances colliding — it's just the kit's intended
            usage: let each instance mint its own id unless you have a reason not
            to. */}
        <RadioGroup
          row
          options={BASIS_OPTIONS}
          value={draft.basis}
          onChange={(value) => patch({ basis: value as ExtraGuestBasis })}
        />
      </fieldset>

      <p className="text-sm text-muted">
        Base occupancy comes from each apartment (its default occupancy), so one strategy fits
        apartments of different sizes. Only adults are charged — children and infants never
        trigger the surcharge or reduce the base. Direct bookings only — OTA channels charge
        their own extra-guest fees, configured on the channel side.
      </p>
    </div>
  );
}
